import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { ConfirmProvider } from '@/hooks/useConfirm'
import { ToastProvider } from '@/hooks/useToast'
// Frontend error reporter (#1241 Swappability rails). App code
// imports from `@/lib/errorReporter` — never from `@sentry/*`
// directly. The adapter's master toggle
// (`VITE_ERROR_REPORTER_ENABLED`) + provider param
// (`VITE_ERROR_REPORTER_PROVIDER`) decide whether the underlying
// Sentry SDK boots. Default off — a build that omits the env vars
// ships with capture disabled.
import { initErrorReporter, ErrorBoundary } from '@/lib/errorReporter'
import { installPreloadErrorRecovery } from '@/lib/preloadRecovery'
import './index.css'

// Frontend error reporter (#1241). No-op when
// VITE_ERROR_REPORTER_ENABLED is false (default), which is the
// configuration for a `pnpm dev` bundle a contributor runs locally.
// When enabled + provider=sentry-saas, the adapter dynamically
// imports the Sentry-backed reporter; that lazy load transitively
// calls `initErrorTracking()` for the SDK init.
initErrorReporter()

// Auto-recover from stale lazy-loaded chunks after a redeploy (#1808).
// When Vite's content-hashed chunks rotate on deploy, a tab still holding
// the old index.html 404s on a lazy import; this reloads once (cooldown-
// guarded against loops) to pull the fresh index.html. Global window
// listener, registered before React renders — not a component concern.
installPreloadErrorRecovery()

// Note: AppContextProvider used to live here, wrapping the entire tree.
// That broke the Phase 3b auth screens — AppContext fetches
// /api/workspaces/me on mount, which throws when no user is signed in
// (no workspace exists for an anonymous visitor). The provider now wraps
// only the workspace routes via a layout route in App.tsx, leaving the
// /auth/* screens free of any workspace assumptions.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* Error boundary at the top of the tree. When wired (i.e. when
        VITE_SENTRY_DSN was set at build time), the boundary captures
        errors thrown during a child's render / constructor / lifecycle
        and ships them to Sentry before rendering the fallback. It
        does NOT catch event-handler errors or async effect errors —
        those are covered separately by the SDK's
        `globalHandlersIntegration` + `browserApiErrorsIntegration`
        (window.onerror, unhandledrejection, wrapped
        setTimeout/addEventListener). When the SDK is not initialized
        the boundary still renders the fallback on a thrown render
        error — it just doesn't ship the event. #1241. */}
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: 24, maxWidth: 720, margin: '40px auto', fontFamily: 'system-ui, sans-serif' }}>
          <h1 style={{ fontSize: 18, marginBottom: 12 }}>Something went wrong.</h1>
          <p style={{ marginBottom: 12 }}>
            We logged the error and will look into it. You can try recovering the page.
          </p>
          <pre style={{ background: '#f5f5f5', padding: 12, fontSize: 12, overflow: 'auto', borderRadius: 6 }}>
            {error instanceof Error ? error.message : String(error)}
          </pre>
          <button onClick={resetError} style={{ marginTop: 12, padding: '8px 16px' }}>
            Reload
          </button>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* App-wide themed confirm + toast (replace native window.confirm
              and window.alert respectively). */}
          <ConfirmProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </ConfirmProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
